// [Review card] Source preview for the "Why this recommendation" dialog. Backed by
// the admin route `GET /review/source-excerpt`: a few clauses of the certified
// template or the precedent a recommendation cites, plus the index of the clause
// that matched. Read-only; the server applies tenant scoping and precedent
// visibility, and answers 404 for anything the caller may not see.
//
// The wire shape mirrors SourceExcerpt in @csai/core, kept inline so the web
// bundle doesn't pull in the core barrel for a type.
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "./api";

export type ReviewSourceExcerptClause = { label: string; text: string };

export type ReviewSourceExcerptMeta = {
  kind: "template" | "precedent";
  dealType: string | null;
  guild: string | null;
  // ISO date and what it is: template "certified" ("effective" when not
  // certified); precedent "executed", else "uploaded".
  date: string | null;
  dateLabel: "certified" | "effective" | "executed" | "uploaded" | null;
  // Precedent party / counterparty names; empty for a template.
  parties: string[];
};

export type ReviewSourceExcerpt = {
  title: string;
  meta: ReviewSourceExcerptMeta;
  clauses: ReviewSourceExcerptClause[];
  // Index of the matching clause WITHIN `clauses`; null when it couldn't be located.
  matchIndex: number | null;
};

export type ReviewSourceRef = {
  kind: "template" | "precedent";
  // recommendation.templateId, or comparables[].precedentId.
  id: string;
  // template: recommendation.standardClauseIndex; precedent: comparables[].clauseId.
  clause?: string | number | null;
};

export const REVIEW_SOURCE_EXCERPT_KEY = ["review-source-excerpt"] as const;

/** Fetch a source excerpt. Pass `null` to hold the query (no source open). */
export function useReviewSourceExcerpt(
  source: ReviewSourceRef | null,
  tenantId?: string
): UseQueryResult<ReviewSourceExcerpt, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [
      ...REVIEW_SOURCE_EXCERPT_KEY,
      source?.kind,
      source?.id,
      source?.clause ?? null,
      tenantId,
    ] as const,
    enabled: source !== null && source.id.length > 0,
    // A cited template / precedent clause is effectively static.
    staleTime: 5 * 60_000,
    queryFn: () => {
      const sp = new URLSearchParams({ kind: source!.kind, id: source!.id });
      if (source!.clause !== undefined && source!.clause !== null) {
        sp.set("clause", String(source!.clause));
      }
      if (tenantId !== undefined && tenantId.length > 0) sp.set("tenantId", tenantId);
      return api<ReviewSourceExcerpt>(`/review/source-excerpt?${sp.toString()}`);
    },
  });
}
